package com.rainbowclubs.rainbow.presentation.login

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.rainbowclubs.rainbow.domain.use_cases.login.CreateAccountUseCase
import com.rainbowclubs.rainbow.presentation.ScreenState
import com.rainbowclubs.rainbow.utils.Validator
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource
import rainbowclubs.composeapp.generated.resources.Res
import rainbowclubs.composeapp.generated.resources.display_name_error
import rainbowclubs.composeapp.generated.resources.email_error
import rainbowclubs.composeapp.generated.resources.password_error
import rainbowclubs.composeapp.generated.resources.repassword_error

class SignupViewModel(
    private val createAccountUseCase: CreateAccountUseCase,
    private val validator: Validator,
) : ViewModel() {

    data class UIModel(
        var username: String = "",
        var password: String = "",
        var rePassword: String = "",
        var displayName: String = "",
        var usernameError: StringResource? = null,
        var passwordError: StringResource? = null,
        var rePasswordError: StringResource? = null,
        var displayNameError: StringResource? = null,
        var passwordVisible: Boolean = false,
        var rePasswordVisible: Boolean = false,
    )

    sealed class SignupNavigationEvent {
        object SelectClub : SignupNavigationEvent()

    }

    private var initialUIModel = UIModel()

    val uiState: StateFlow<ScreenState<UIModel>>
        field: MutableStateFlow<ScreenState<UIModel>> = MutableStateFlow(
            ScreenState.Content<UIModel>(initialUIModel)
        )

    private val navigationIntentChannel = Channel<SignupNavigationEvent>(Channel.BUFFERED)
    val navigationIntent: Flow<SignupNavigationEvent> = navigationIntentChannel.receiveAsFlow()

    fun onUsernameChanged(username: String) {
        initialUIModel = initialUIModel.copy(username = username, usernameError = null)
        uiState.value = ScreenState.Content(initialUIModel)

    }

    fun onPasswordChanged(password: String) {
        initialUIModel = initialUIModel.copy(password = password, passwordError = null)
        uiState.value = ScreenState.Content(initialUIModel)
    }

    fun onRePasswordChanged(password: String) {
        initialUIModel = initialUIModel.copy(rePassword = password, rePasswordError = null)
        uiState.value = ScreenState.Content(initialUIModel)
    }

    fun onDisplayNameChanged(displayName: String) {
        initialUIModel = initialUIModel.copy(displayName = displayName, displayNameError = null)
        uiState.value = ScreenState.Content(initialUIModel)
    }

    fun onCreateAccountClicked() {
        if (!validator.validateEmailFormat(initialUIModel.username)
            || initialUIModel.username.isBlank()
        ) {
            initialUIModel = initialUIModel.copy(usernameError = Res.string.email_error)
            uiState.value = ScreenState.Content(initialUIModel)
        } else {
            initialUIModel = initialUIModel.copy(usernameError = null)
            uiState.value = ScreenState.Content(initialUIModel)
        }

        if (!validator.validatePassword(initialUIModel.password)
            || initialUIModel.password.isBlank()
        ) {
            initialUIModel =
                initialUIModel.copy(passwordError = Res.string.password_error)
            uiState.value = ScreenState.Content(initialUIModel)
        } else {
            initialUIModel = initialUIModel.copy(passwordError = null)
        }

        if (!validator.validateRePassword(initialUIModel.password, initialUIModel.rePassword)
            || initialUIModel.rePassword.isBlank()
        ) {
            initialUIModel = initialUIModel.copy(rePasswordError = Res.string.repassword_error)
            uiState.value = ScreenState.Content(initialUIModel)
        } else {
            initialUIModel = initialUIModel.copy(passwordError = null)
        }

        if (initialUIModel.displayName.isBlank()) {
            initialUIModel = initialUIModel.copy(displayNameError = Res.string.display_name_error)
            uiState.value = ScreenState.Content(initialUIModel)
        }

        if (
            validator.validateEmailFormat(initialUIModel.username) &&
            validator.validatePassword(initialUIModel.password) &&
            validator.validateRePassword(initialUIModel.password, initialUIModel.rePassword)
        ) {
            viewModelScope.launch {
                uiState.value = ScreenState.Loading
                createAccountUseCase(
                    initialUIModel.username.trim(),
                    initialUIModel.password.trim(),
                    displayName = initialUIModel.displayName.trim(),
                ).onSuccess { authResult ->
                    navigationIntentChannel.send(SignupNavigationEvent.SelectClub)
                }.onFailure {
                    uiState.value = ScreenState.Error(it)
                }
            }
        }
    }

    fun tryAgain() {
        uiState.value = ScreenState.Content(initialUIModel)
    }

    fun onShowPasswordClicked() {
        initialUIModel = initialUIModel.copy(passwordVisible = !initialUIModel.passwordVisible)
        uiState.value = ScreenState.Content(initialUIModel)
    }

    fun onShowRepasswordClicked() {
        initialUIModel = initialUIModel.copy(rePasswordVisible = !initialUIModel.rePasswordVisible)
        uiState.value = ScreenState.Content(initialUIModel)
    }
}